<template>
  <CollapsibleCard
    ref="collapsible"
    :editable="editable && canBeEditedHasCourses"
    v-if="!(selectedApplication && selectedApplication.allCoursesApproved)"
  >
    <template v-slot:header> 
      {{applicationOrders && applicationOrders.length ? 'DISTANSUTBILDNING' : 'FÖRESLAGEN DISTANSUTBILDNING'}}
    </template>

    <template v-slot:info>{{ getHeaderInfo }}</template>

    <template v-slot:editinfo> Systemet har föreslagit en distansutbildning baserat på personens meriter. </template>

    <template v-slot:view>
      <ApplicationDistanceEducationView :items="items" />
    </template>

    <template v-slot:edit>
      <ApplicationDistanceEducationEdit
        :items="items"
        ref="edit"
        @checked="selectedCourses = $event"
      />
    </template>

    <template v-slot:cancel>
      <v-btn
        color="secondary"
        @click="$refs.edit.cancel()"
      >
        <v-icon>$sl-cancel</v-icon>Avbryt
      </v-btn>
    </template>

    <v-card-text class="d-flex justify-end pr-0">
      <v-btn
        :disabled="!canCreateOrder"
        color="primary"
        @click="createOrder()"
        v-if="get(applicationProfession, 'distanceEducationProvider.features.hasCourses', false)"
      >
        <v-icon>$sl-notes-paper-approve</v-icon>SKAPA BESTÄLLNING
      </v-btn>
      
      <v-btn v-if="canUndoApprove" class="ml-3"
        @click="undoCreateDistanceOrder()"><v-icon>$sl-cancel</v-icon>Ångra
      </v-btn>
      
      <v-btn
        color="primary"
        @click="approveWithoutOrder()"
        v-if="!get(applicationProfession, 'distanceEducationProvider.features.hasCourses', false)"
        :disabled="!approvedStateByCompany"
      >
        <v-icon>$sl-notes-paper-approve</v-icon>GODKÄNN ANMÄLAN UTAN DISTANSBESTÄLLNING
      </v-btn>

    </v-card-text>

    <v-card-text
      class="mr-0 pr-4 pl-0"
      v-if="applicationOrders && applicationOrders.length && get(applicationProfession, 'distanceEducationProvider.features.hasCourses', false)"
    >
      <h6 class="font-weight-bold">Historik</h6>
      <div
        v-for="(item, index) in applicationOrders"
        :key="index"
        class="d-flex justify-space-between"
      >
        <div>
          <span>{{ item.createdAt | date }}</span>:
          <span v-if="item.stateId === 7">Beställning {{ item.orderId }} skapad, ej godkänd </span>
          <span v-else><a @click="showOrder(item)">Beställning {{ item.orderId }}</a> skapad </span>
          <span><a @click="showUser(item.createdById)">({{ getCreator(item) }})</a></span>
        </div>
        <div>
          {{ orderSum(item) | currency | withCurrencyCode }}
        </div>
      </div>
    </v-card-text>

  </CollapsibleCard>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import {RulesMixin, CanIDo} from '@/components/mixins'
import find from 'lodash/find'
import filter from 'lodash/filter'
import sumBy from 'lodash/sumBy'
import get from 'lodash/get'
import MessageBox from '@/components/common/MessageBox.vue'
import CollapsibleCard from '@/components/common/CollapsibleCard'
import ApplicationDistanceEducationView from './ApplicationDistanceEducationView.vue'
import ApplicationDistanceEducationEdit from './ApplicationDistanceEducationEdit.vue'
import API from '@/services/api.js'
import SugarDate from 'sugar'
import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'

// @group Application
// ApplicationDistanceEducation component
export default {
  name: 'ApplicationDistanceEducation',

  mixins: [RulesMixin, CanIDo],

  components: {
    MessageBox,
    CollapsibleCard,
    ApplicationDistanceEducationView,
    ApplicationDistanceEducationEdit
  },

  props: {
    editable: {
      type: Boolean,
      default: false
    }
  },

  mounted() {

    //- Workaround to re-render Child-component
    this.$refs.collapsible.editClick()
    this.$nextTick(() => {
      this.$refs.collapsible.editMode = false
    })
  },

  data: () => ({
    open: [],
    order: {
      selectedDistanceOrderRows: []
    },
    selectedCourses: []
  }),

  computed: {
    ...mapGetters('application', [
      'selectedApplication',
      'applicationProfession',
      'applicationOrders',
      'applicationDistanceEductions',
      'applicationWorkplace',
      'canBeEdited',
      'approvedStateByCompany'
    ]),

    ...mapGetters('basic', ['getStateByStatename', 'approvedByByn']),
    ...mapGetters('currentuser', ['currentUser', 'currentUserRole']),
    ...mapGetters('basic', ['orderedState']),

    canBeEditedHasCourses() {
      return Boolean(this.canBeEdited && get(this, 'applicationProfession.distanceEducationProvider.features.hasCourses',false))
    },

    getHeaderInfo() {
      if (this.applicationOrders && this.applicationOrders.length > 0 && !get(this, 'applicationProfession.distanceEducationProvider.features.hasCourses', false)) {
        return 'Godkänd utan beställning'
      }
      if (this.applicationOrders && this.applicationOrders.length === 1) {
        return 'Beställning skapad'
      }
      if (this.applicationOrders && this.applicationOrders.length > 1) {
        return `${this.applicationOrders.length} beställning(ar)`
      }

      return 'Ingen beställning skapad'
    },

    items() {
      let it = []

      if(this.applicationOrders && this.applicationOrders.length){
        return this.applicationOrders.reduce((acc, currApllicationOrder) => {

          acc.push(...currApllicationOrder.distanceOrderRows.reduce((accDistanceRow, currDistanceRow) => {
            accDistanceRow.push({
              id: currDistanceRow.remoteId,
              name: currDistanceRow.name,
              type: 1,
              price: currDistanceRow.price,
              children: uniqBy(orderBy(currDistanceRow.assignments, ['id']), 'id')
            })
            return accDistanceRow;
          }, []))

          return acc;
        }, [])
      }

      (this.applicationDistanceEductions || []).forEach(el => {
        this.open.push(el.name)
        if (el.assignments && Array.isArray(el.assignments)) {
          it.push({
            id: el.id,
            name: el.name,
            type: el.type,
            price: el.price,
            assignmentsPrice: el.assignmentsPrice,
            selected: this.isSelected(this.applicationOrders, el, this.applicationDistanceEductions),
            children: el.assignments.map(o => {
              return {
                id: `${el.id}.${o.id}`,
                parentId: el.id,
                parentName: el.name,
                assignmentId: o.id,
                name: o.name,
                price: o.price
              }
            })
          })
        } else {
          it.push({
            id: el.id,
            name: el.name,
            type: el.type,
            price: el.price,
            assignmentsPrice: el.assignmentsPrice,
            selected: this.isSelected(this.applicationOrders, el, this.applicationDistanceEductions)
          })
        }
      })
      
      this.selectedCourses = it

      return it
    },

    canUndoApprove(){
      return this.applicationOrders[0] && this.applicationOrders[0].createdById === this.currentUser.id
    }
  },

  asyncComputed: {
    canCreateOrder(){
      return (filter(this.selectedCourses, ['selected', true]).length > 0 && this.approvedStateByCompany) && this.canIDo('order', 'create');
    },
  },

  methods: {
    ...mapActions('application', ['updateSelectedApplication', 'setApplicationOrders']),
    ...mapActions('user', ['setSelectedUser']),
    ...mapActions('order', ['setSelectedOrder']),

    get: get,

    undoCreateDistanceOrder(){
      API.undoCreateDistanceOrder(this.selectedApplication.id).finally(() => {
        this.$emit('saved');
      })
    },

    // @vuese
    // Used to create distance order
    // @arg The argument is object representing order
    async createOrder() {

      const today = new Date().setDate(new Date().getDate() - 1)

      if( SugarDate.Date.isBefore(new Date(this.selectedApplication?.employmentDate), today) ) {
        const res = await this.$dialog.confirm({
          text: 'Observera att startdatum för denna anmälan ligger före dagens datum. För att godkänna denna anmälan, bekräfta att ni är medvetna om detta.',
          title: 'Godkänn anmälan',
          actions: [
            {text: 'AVBRYT', key: false, icon: '$sl-cancel'},
            {text: 'GODKÄNN', key: true, icon: '$sl-delete', outlined: true}
          ]
        })
  
        if (res !== true) return
      }

      let selectedCourses = filter(this.selectedCourses, ['selected', true])
      if (selectedCourses.length < 1) {
        this.$events.fire(
          'showError',
          'Du måste välja en eller flera utbildningsmoduler innan du kan skicka beställningen.'
        )
        return
      }

      const shouldReviewAttachments = this.selectedApplication.applicationDocuments.some(
        doc => doc.applicationDocumentTypeId === 1
      )

      if (shouldReviewAttachments) {
        const isAttachementReviewed = await this.$dialog.confirm({
          title: 'HAR DU KONTROLLERAT BETYG OCH INTYG?',
          text:
            'Var vänlig öppna bifogade filer och kontrollera att betyg och intyg är giltiga samt stämmer överens med de kursresultat som visas innan du godkänner.',
          width: '696',
          actions: [
            {text: 'Avbryt', key: false, icon: '$sl-cancel', outlined: true},
            {text: 'FORTSÄTT', key: true, icon: '$sl-check'}
          ]
        })

        if (isAttachementReviewed !== true) {
          return
        }
      }

      let createOrderObject = {
        applicationId: this.selectedApplication.id,
        companyId: this.selectedApplication.companyId,
        professionId: this.selectedApplication.professionId,
        distanceOrderRows: this.selectedCourses.map(({children, ...selectedCourse}) => ({
          ...selectedCourse,
          assignments: !children ? null : children.map(ch => ({ id: ch.assignmentId, name: ch.name }))
        })),
        regionId:
          this.applicationWorkplace && this.applicationWorkplace.regionId ?
            this.applicationWorkplace.regionId :
            this.applicationCompany.regionId,
        distanceEducationProviderId: this.applicationProfession.distanceEducationProviderId
      }

      API.createDistanceOrder({
        data: createOrderObject
      })
        .then(response => {
          this.$events.fire('showInfo', 'Skickad för godkännande.')
          this.order = {
            selectedDistanceOrderRows: [],
            professionPackage: null
          }

          //- Backend will set state to "ordered"
          const ordered = this.getStateByStatename('ordered')
          this.updateSelectedApplication({
            stateId: ordered.id,
            state: {id: ordered.id, name: ordered.name},
            orderedBy: {id: this.currentUser.id}
          })

          //- Refresh orders
          API.listDistanceOrders({
            startAt: 0,
            pageSize: 50,
            advSearch: {applicationId: this.selectedApplication.id}
          }).then(({data}) => {
            this.setApplicationOrders(data.rows)
          })
        })
        .catch(error => {
          if (get(error, 'response.status', '') != '422') {
            this.$events.fire(
              'showError',
              'Beställningen kunde inte skickas. Detta kan vara ett tillfälligt fel, försök igen om några minuter.'
            )
          }
          console.log('There was an error:', error)
        })
    },

    async approveWithoutOrder() {

      const shouldReviewAttachments = this.selectedApplication.applicationDocuments.some(
        doc => doc.applicationDocumentTypeId === 1
      )

      if (shouldReviewAttachments) {
        const isAttachementReviewed = await this.$dialog.confirm({
          title: 'HAR DU KONTROLLERAT BETYG OCH INTYG?',
          text:
            'Var vänlig öppna bifogade filer och kontrollera att betyg och intyg är giltiga samt stämmer överens med de kursresultat som visas innan du godkänner.',
          width: '696',
          actions: [
            {text: 'Avbryt', key: false, icon: '$sl-cancel', outlined: true},
            {text: 'FORTSÄTT', key: true, icon: '$sl-check'}
          ]
        })

        if (isAttachementReviewed !== true) {
          return
        }
      }

      let createOrderObject = {
        applicationId: this.selectedApplication.id,
        companyId: this.selectedApplication.companyId,
        professionId: this.selectedApplication.professionId,
        distanceOrderRows: [],
        regionId:
          this.applicationWorkplace && this.applicationWorkplace.regionId ?
            this.applicationWorkplace.regionId :
            this.applicationCompany.regionId,
        distanceEducationProviderId: this.applicationProfession.distanceEducationProviderId
      }

      API.createDistanceOrder({
        data: createOrderObject
      })
      .then(response => {
        this.$events.fire('showInfo', 'Skickad för godkännande.')
        this.order = {
          selectedDistanceOrderRows: [],
          professionPackage: null
        }

        //- Backend will set state to "ordered"
        const ordered = this.getStateByStatename('ordered')
        this.updateSelectedApplication({
          stateId: ordered.id,
          state: {id: ordered.id, name: ordered.name},
          orderedBy: {id: this.currentUser.id}
        })
      })
    },

    async showUser(id) {
      await this.setSelectedUser(null)
      this.$nextTick(() => {
        this.$router.push({name: 'User', params: {id}})
      })
    },

    getCreator(item) {
      const fname = get(item, 'createdBy.firstName', '')
      const lname = get(item, 'createdBy.lastName', '')
      return `${fname} ${lname}`
    },

    async showOrder(item) {
      await this.setSelectedOrder(null)
      this.$nextTick(() => {
        this.$router.push({name: 'OrderDetail', params: {id: item.id}})
      })
    },

    orderSum(item) {
      const rows = item.distanceOrderRows || []
      return sumBy(rows, 'price')
    },

    isSelected(orders, el, distanceOrderRows) {
      if (el.remoteId === 'INTRO') {
        return true
      }

      //- No order, return default
      if(filter(distanceOrderRows, ['type', 3]).length === 0){
        return true
      }
      const { sumType12, sumType3 } = distanceOrderRows.reduce((acc, row) => {
        if(row.type === 3){
          return {
            ...acc,
            sumType3: row.price
          }
        }

        return {
          ...acc,
          sumType12: acc.sumType12 + Math.min(row.assignmentsPrice || row.price, row.price)
        }
      }, {
        sumType12: 0, 
        sumType3: 0
      })

      //- If it's a combined course ( type 3 ) then always pre-select it
      return el.type === 3 ? true : sumType3 > sumType12
      // return el.type === 3 ? sumType12 >= sumType3 : sumType3 > sumType12
    }
  }
}
</script>

<style >
.v-messages__wrapper {
  display: none !important;
}
.linkOrder {
  font-size: 15px;
}

.v-treeview-node__children div > button {
  display: none !important;
}
.d_none {
  display: none !important;
}
</style>
